import { nextTick, ref, watch, type Ref } from 'vue';

/**
 * Composable to handle automatic scrolling when accordion tabs are opened.
 * PrimeVue's Accordion doesn't scroll to opened tabs by default, which can be
 * confusing when opening tabs near the bottom of the viewport.
 *
 * @param initialActiveIndex - Initial active index (number for single, array for multiple mode)
 * @returns Object with activeIndex ref, headerRefs, and setHeaderRef function
 *
 * @example
 * ```vue
 * <script setup>
 * import { useAccordionScroll } from '@/composables/useAccordionScroll';
 *
 * const { activeIndex, headerRefs, setHeaderRef } = useAccordionScroll([0]);
 * </script>
 *
 * <template>
 *   <Accordion v-model:active-index="activeIndex" :multiple="true">
 *     <AccordionTab v-for="(item, idx) in items" :key="item.id">
 *       <template #header>
 *         <div :ref="(el) => setHeaderRef(el as HTMLElement | null, idx)">
 *           {{ item.name }}
 *         </div>
 *       </template>
 *       <!-- content -->
 *     </AccordionTab>
 *   </Accordion>
 * </template>
 * ```
 */
export function useAccordionScroll<T extends number | number[] | null>(initialActiveIndex: T = null as T) {
  const activeIndex: Ref<T> = ref(initialActiveIndex) as Ref<T>;
  const headerRefs = ref<HTMLElement[]>([]);
  let previousActiveIndex: number[] = Array.isArray(initialActiveIndex)
    ? [...initialActiveIndex]
    : initialActiveIndex !== null
      ? [initialActiveIndex]
      : [];

  const setHeaderRef = (el: HTMLElement | null, idx: number) => {
    if (el) headerRefs.value[idx] = el;
  };

  watch(
    () => activeIndex.value,
    async (newValue) => {
      await nextTick();

      // Convert to array for consistent handling
      const newIndices: number[] = Array.isArray(newValue) ? newValue : newValue !== null ? [newValue as number] : [];

      // Find the newly opened tab (present in new but not in previous)
      const newlyOpened = newIndices.filter((idx) => !previousActiveIndex.includes(idx));

      if (newlyOpened.length > 0) {
        const index = newlyOpened[newlyOpened.length - 1];
        if (headerRefs.value[index]) {
          // Small delay to allow accordion animation to start
          setTimeout(() => {
            headerRefs.value[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
      }

      previousActiveIndex = [...newIndices];
    },
    { deep: true },
  );

  return {
    activeIndex,
    headerRefs,
    setHeaderRef,
  };
}
